// Mesh profile deformation — extract a "vase-like" silhouette profile from any
// triangle mesh, then deform the original mesh by scaling each vertex's radial
// distance based on the ratio of (new profile r at vertex Z) / (original profile r).
//
// Works best on axially-near-symmetric models (vases, bottles, cups). Non-symmetric
// models still deform sensibly — the shape gets "fatter" or "thinner" along Z bands —
// but won't preserve fine asymmetric features.

import type { Triangle, Vec3 } from '../core/types';
import type { ProfilePoint } from '../core/vaseModel';

/** Sample N control points from a mesh by binning vertices along Z and taking
 *  the MAX radial distance per bin (the silhouette). */
export function extractMeshProfile(tris: Triangle[], pointCount = 5): ProfilePoint[] {
  if (tris.length === 0) return [];
  let minZ = Infinity, maxZ = -Infinity;
  for (const t of tris) {
    for (const v of [t.a, t.b, t.c]) {
      if (v[2] < minZ) minZ = v[2];
      if (v[2] > maxZ) maxZ = v[2];
    }
  }
  if (!isFinite(minZ) || maxZ <= minZ) return [];
  // Bin into many slots first for accuracy, then resample to pointCount.
  const BINS = 64;
  const bins = new Float32Array(BINS);
  for (const t of tris) {
    for (const v of [t.a, t.b, t.c]) {
      const r = Math.hypot(v[0], v[1]);
      const bi = Math.min(BINS - 1, Math.max(0, Math.floor(((v[2] - minZ) / (maxZ - minZ)) * BINS)));
      if (r > bins[bi]) bins[bi] = r;
    }
  }
  // Forward + backward fill empty bins from neighbours
  for (let i = 1; i < BINS; i++) if (bins[i] === 0) bins[i] = bins[i - 1];
  for (let i = BINS - 2; i >= 0; i--) if (bins[i] === 0) bins[i] = bins[i + 1];

  const out: ProfilePoint[] = [];
  const N = Math.max(2, pointCount);
  for (let i = 0; i < N; i++) {
    const t = i / (N - 1);
    const z = minZ + t * (maxZ - minZ);
    const bi = Math.min(BINS - 1, Math.floor(t * BINS));
    out.push({ z, r: Math.max(0.5, bins[bi]) });
  }
  return out;
}

/** Linear-interpolate a profile (sorted by Z) to get r at arbitrary z. */
function interpR(pts: ProfilePoint[], z: number): number {
  if (z <= pts[0].z) return pts[0].r;
  if (z >= pts[pts.length - 1].z) return pts[pts.length - 1].r;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i], b = pts[i + 1];
    if (z >= a.z && z <= b.z) {
      const t = (z - a.z) / Math.max(1e-6, b.z - a.z);
      return a.r + (b.r - a.r) * t;
    }
  }
  return pts[pts.length - 1].r;
}

/** Deform every vertex of `originalTris` by scaling its (x,y) radius by
 *  newProfile(z) / originalProfile(z). Triangle normals are recomputed. */
export function deformMeshByProfile(
  originalTris: Triangle[],
  originalProfile: ProfilePoint[],
  newProfile: ProfilePoint[]
): Triangle[] {
  if (originalProfile.length < 2 || newProfile.length < 2) return originalTris.slice();
  const sortedOrig = originalProfile.slice().sort((a, b) => a.z - b.z);
  const sortedNew = newProfile.slice().sort((a, b) => a.z - b.z);
  const out: Triangle[] = new Array(originalTris.length);

  function scaleVert(v: Vec3): Vec3 {
    const r0 = interpR(sortedOrig, v[2]);
    if (r0 < 1e-3) return [v[0], v[1], v[2]];
    const r1 = interpR(sortedNew, v[2]);
    const m = r1 / r0;
    return [v[0] * m, v[1] * m, v[2]];
  }

  for (let i = 0; i < originalTris.length; i++) {
    const t = originalTris[i];
    const a = scaleVert(t.a);
    const b = scaleVert(t.b);
    const c = scaleVert(t.c);
    // Recompute face normal
    const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2];
    const vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2];
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const l = Math.hypot(nx, ny, nz) || 1;
    nx /= l; ny /= l; nz /= l;
    out[i] = { a, b, c, normal: [nx, ny, nz] };
  }
  return out;
}
